import { useState, useMemo, useEffect } from 'react';
import { client } from '@/api/client';
import { DollarSign, CheckCircle2, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn, formatFinalizedDate } from '@/lib/utils';
import RecordPaymentModal from './RecordPaymentModal';
import { payDueBucket, numOrDefault } from '@/lib/invoiceHelpers';

const PAY_DUE_STYLES = {
  green: 'text-green-700 dark:text-green-400 bg-green-500/15 dark:bg-green-500/10 border-green-500/40 dark:border-green-500/30',
  yellow: 'text-amber-700 dark:text-amber-400 bg-amber-500/15 dark:bg-amber-500/10 border-amber-500/40 dark:border-amber-500/30',
  red: 'text-red-700 dark:text-red-400 bg-red-500/15 dark:bg-red-500/10 border-red-500/40 dark:border-red-500/30',
};
const PAY_DUE_LABELS = { green: 'Due soon', yellow: 'Due today', red: 'Overdue' };

export default function PayrollTab({ settlements, onRefresh }) {
  const [expanded, setExpanded] = useState({});
  const [payingDriver, setPayingDriver] = useState(null);
  const [weeklyInsurance, setWeeklyInsurance] = useState(0);
  const [weeklyEld, setWeeklyEld] = useState(0);
  const [driverPaymentMap, setDriverPaymentMap] = useState({}); // email → { truck, trailer }
  const [lineItemsMap, setLineItemsMap] = useState({}); // settlement_id → [items]
  const [personalAdvancesByDriver, setPersonalAdvancesByDriver] = useState({}); // email → [advances]

  useEffect(() => {
    client.entities.CompanySettings.list().then(settings => {
      const ins = settings.find(s => s.key === 'insurance_deduction');
      const eld = settings.find(s => s.key === 'eld_weekly_cost');
      setWeeklyInsurance(parseFloat(ins?.value) || 0);
      setWeeklyEld(parseFloat(eld?.value) || 0);
    });
    client.entities.DriverProfile.list('-created_date', 200).then(profiles => {
      const map = {};
      for (const d of profiles) {
        if (d.user_email) map[d.user_email] = {
          truck: d.weekly_truck_payment || 0,
          trailer: d.weekly_trailer_payment || 0,
          // Insurance/ELD (below) are the driver's OWN truck insurance +
          // ELD subscription -- real costs only an Owner Operator
          // carries. A Company Driver runs carrier-owned, carrier-insured
          // equipment, so these should never be deducted from their pay.
          isOwnerOperator: d.driver_type === 'Owner Operator',
        };
      }
      setDriverPaymentMap(map);
    });
    // Fetch all line items for in-batch settlements
    client.entities.SettlementLineItem.list('-created_date', 500).then(items => {
      const map = {};
      for (const item of items) {
        if (!map[item.settlement_id]) map[item.settlement_id] = [];
        map[item.settlement_id].push(item);
      }
      setLineItemsMap(map);
    });
    // Personal advances aren't per-load (see ActiveLoad.jsx's comment on
    // why they're not deducted at delivery) -- they're deducted here, at
    // payroll time, once per driver. applied_payment_reference is null
    // until a payroll run actually uses one, so this only ever picks up
    // advances that haven't been applied yet (no double-deduction risk).
    client.entities.FuelAdvance.filter({ advance_type: 'personal', status: 'Approved' }, '-approved_date', 500).then(advances => {
      const map = {};
      for (const a of advances) {
        if (a.applied_payment_reference) continue;
        if (!map[a.driver_email]) map[a.driver_email] = [];
        map[a.driver_email].push(a);
      }
      setPersonalAdvancesByDriver(map);
    });
  }, []);

  // Group In Batch settlements by driver
  const unpaidByDriver = useMemo(() => {
    const inBatch = settlements.filter(s => s.status === 'In Batch');
    const map = {};
    for (const s of inBatch) {
      if (!map[s.driver_email]) {
        map[s.driver_email] = { name: s.driver_name, email: s.driver_email, settlements: [] };
      }
      map[s.driver_email].settlements.push(s);
    }
    return Object.values(map);
  }, [settlements]);

  const toggle = (email) => setExpanded(prev => ({ ...prev, [email]: !prev[email] }));

  if (unpaidByDriver.length === 0) {
    return (
      <div className="glass-card p-12 text-center">
        <CheckCircle2 className="w-12 h-12 text-green-400 mx-auto mb-3" />
        <p className="font-space font-semibold text-foreground text-lg">All Caught Up!</p>
        <p className="text-muted-foreground text-sm mt-1">No finalized settlements are awaiting payment.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        {unpaidByDriver.length} driver{unpaidByDriver.length !== 1 ? 's' : ''} in payroll batch
      </p>

      {unpaidByDriver.map(driver => {
        // final_net_pay is set at approval time and includes all per-load deductions + manual adjustments.
        // Weekly fixed (insurance + ELD + driver truck/trailer) are NOT included — subtracted once here at payroll.
        const loadsTotal = driver.settlements.reduce((sum, s) => sum + (s.final_net_pay ?? s.net_driver_pay ?? 0), 0);
        const driverPayments = driverPaymentMap[driver.email] || {};
        const personalAdvances = personalAdvancesByDriver[driver.email] || [];
        const personalAdvanceTotal = personalAdvances.reduce((sum, a) => sum + (a.amount || 0), 0);
        const driverWeeklyInsurance = driverPayments.isOwnerOperator ? weeklyInsurance : 0;
        const driverWeeklyEld = driverPayments.isOwnerOperator ? weeklyEld : 0;
        const weeklyFixed = driverWeeklyInsurance + driverWeeklyEld + (driverPayments.truck || 0) + (driverPayments.trailer || 0) + personalAdvanceTotal;
        const total = Math.max(0, loadsTotal - weeklyFixed);
        const isOpen = expanded[driver.email];
        // Most urgent due date across this driver's batch -- red trumps
        // yellow trumps green, matching what actually needs attention.
        const dueDates = driver.settlements.map(s => s.pay_due_at).filter(Boolean).sort();
        const earliestDueBucket = dueDates.length ? payDueBucket(dueDates[0]) : null;

        return (
          <div key={driver.email} className="glass-card overflow-hidden">
            {/* Driver header row */}
            <button
              className="w-full flex items-center justify-between px-5 py-4 hover:bg-white/3 transition-colors"
              onClick={() => toggle(driver.email)}
            >
              <div className="flex items-center gap-3">
                <div className="text-left">
                  <p className="font-semibold text-foreground flex items-center gap-2">
                    {driver.name}
                    {earliestDueBucket && (
                      <span className={cn("text-[10px] font-semibold px-2 py-0.5 rounded-full border", PAY_DUE_STYLES[earliestDueBucket])}>
                        {PAY_DUE_LABELS[earliestDueBucket]}
                      </span>
                    )}
                  </p>
                  <p className="text-xs text-muted-foreground">{driver.settlements.length} settlement{driver.settlements.length !== 1 ? 's' : ''} in batch</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="text-right">
                  <p className="text-xs text-muted-foreground">Net Due (after deductions)</p>
                  <p className="font-bold text-green-400 text-lg">${total.toFixed(2)}</p>
                </div>
                <Button
                  size="sm"
                  onClick={e => { e.stopPropagation(); setPayingDriver({ ...driver, personalAdvances }); }}
                  className="bg-primary text-primary-foreground h-8 px-3 text-xs"
                >
                  <DollarSign className="w-3 h-3 mr-1" /> Record Payment
                </Button>
                {isOpen ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
              </div>
            </button>

            {/* Expanded load list */}
            {isOpen && (
              <div className="border-t border-border bg-black/20">
                {driver.settlements.map(s => {
                  const gross = s.gross_revenue || 0;
                  const carrierFee = numOrDefault(s.carrier_take_amount, gross * 0.15);
                  const dispatchFee = s.dispatch_fee_amount || 0;
                  const fuelAdvance = s.fuel_advance_total || 0;
                  const lumperFee = s.lumper_fee_deducted || 0;
                  const lateFee = s.late_fee_deducted || 0;
                  const manualItems = lineItemsMap[s.id] || [];
                  const manualDeductions = manualItems.filter(i => i.type === 'deduction');
                  const manualEarnings = manualItems.filter(i => i.type === 'earning');
                  const net = s.final_net_pay ?? s.net_driver_pay ?? 0;
                  const dueBucket = payDueBucket(s.pay_due_at);

                  return (
                    <div key={s.id} className="px-5 py-4 border-b border-border text-sm space-y-2">
                      {/* Load header */}
                      <div className="flex justify-between items-center">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold text-foreground">Load #{s.load_number}</span>
                          <span className="text-muted-foreground text-xs">— Finalized {formatFinalizedDate(s.finalized_date)}</span>
                          {dueBucket && (
                            <span className={cn("text-[10px] font-semibold px-1.5 py-0.5 rounded-full border", PAY_DUE_STYLES[dueBucket])}>
                              {PAY_DUE_LABELS[dueBucket]}
                            </span>
                          )}
                        </div>
                        <span className="text-xs text-muted-foreground">Gross: ${gross.toLocaleString()}</span>
                      </div>

                      {/* Per-load deductions breakdown */}
                      <div className="rounded-xl bg-muted/40 border border-border px-4 py-3 space-y-1.5 text-xs">
                        <div className="flex justify-between text-muted-foreground">
                          <span>Carrier Fee ({numOrDefault(s.carrier_take_percent, 15)}%)</span>
                          <span className="text-red-400">−${carrierFee.toFixed(2)}</span>
                        </div>
                        {dispatchFee > 0 && (
                          <div className="flex justify-between text-muted-foreground">
                            <span>Dispatch Fee ({numOrDefault(s.dispatch_fee_percent, 3)}%)</span>
                            <span className="text-red-400">−${dispatchFee.toFixed(2)}</span>
                          </div>
                        )}
                        {fuelAdvance > 0 && (
                          <div className="flex justify-between text-muted-foreground">
                            <span>Fuel Advance</span>
                            <span className="text-red-400">−${fuelAdvance.toFixed(2)}</span>
                          </div>
                        )}
                        {lumperFee > 0 && (
                          <div className="flex justify-between text-muted-foreground">
                            <span>Lumper Fee</span>
                            <span className="text-red-400">−${lumperFee.toFixed(2)}</span>
                          </div>
                        )}
                        {lateFee > 0 && (
                          <div className="flex justify-between text-muted-foreground">
                            <span>Late Fee</span>
                            <span className="text-red-400">−${lateFee.toFixed(2)}</span>
                          </div>
                        )}
                        {manualDeductions.map((item, i) => (
                          <div key={i} className="flex justify-between text-muted-foreground">
                            <span>{item.category} — {item.memo}</span>
                            <span className="text-red-400">−${Math.abs(item.amount || 0).toFixed(2)}</span>
                          </div>
                        ))}
                        {manualEarnings.map((item, i) => (
                          <div key={i} className="flex justify-between text-muted-foreground">
                            <span>{item.category} — {item.memo}</span>
                            <span className="text-green-400">+${Math.abs(item.amount || 0).toFixed(2)}</span>
                          </div>
                        ))}
                        <div className="border-t border-border pt-1.5 flex justify-between font-semibold text-foreground">
                          <span>Net to Driver (this load)</span>
                          <span className="text-green-400">${net.toFixed(2)}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}

                {/* Weekly deductions — applied once per pay period */}
                {weeklyFixed > 0 && (
                  <div className="mx-5 mb-3 rounded-xl bg-blue-500/8 border border-blue-500/20 px-4 py-3 space-y-1.5 text-xs">
                    <p className="font-semibold text-blue-300 mb-1">Weekly Fixed Deductions (applied once this pay period)</p>
                    {driverWeeklyInsurance > 0 && (
                      <div className="flex justify-between text-blue-300/80">
                        <span>Insurance</span>
                        <span>−${driverWeeklyInsurance.toFixed(2)}</span>
                      </div>
                    )}
                    {driverWeeklyEld > 0 && (
                      <div className="flex justify-between text-blue-300/80">
                        <span>ELD</span>
                        <span>−${driverWeeklyEld.toFixed(2)}</span>
                      </div>
                    )}
                    {(driverPayments.truck || 0) > 0 && (
                      <div className="flex justify-between text-blue-300/80">
                        <span>Truck Payment</span>
                        <span>−${driverPayments.truck.toFixed(2)}</span>
                      </div>
                    )}
                    {(driverPayments.trailer || 0) > 0 && (
                      <div className="flex justify-between text-blue-300/80">
                        <span>Trailer Payment</span>
                        <span>−${driverPayments.trailer.toFixed(2)}</span>
                      </div>
                    )}
                    {personalAdvanceTotal > 0 && (
                      <div className="flex justify-between text-blue-300/80">
                        <span>Personal Advance{personalAdvances.length > 1 ? 's' : ''}</span>
                        <span>−${personalAdvanceTotal.toFixed(2)}</span>
                      </div>
                    )}
                    <div className="border-t border-blue-500/20 pt-1.5 flex justify-between font-semibold text-blue-300">
                      <span>Total Weekly Fixed</span>
                      <span>−${weeklyFixed.toFixed(2)}</span>
                    </div>
                  </div>
                )}

                {/* Batch total */}
                <div className="flex justify-between items-center px-5 py-3 bg-white/3">
                  <div>
                    <span className="text-xs text-muted-foreground">Loads subtotal: ${loadsTotal.toFixed(2)}</span>
                    {weeklyFixed > 0 && (
                      <span className="text-xs text-red-400 ml-3">- Weekly fixed: ${weeklyFixed.toFixed(2)}</span>
                    )}
                  </div>
                  <span className="text-sm font-bold text-foreground">
                    Net Due: <span className="text-green-400">${total.toFixed(2)}</span>
                  </span>
                </div>
              </div>
            )}
          </div>
        );
      })}

      {payingDriver && (
        <RecordPaymentModal
          driver={payingDriver}
          settlements={payingDriver.settlements}
          personalAdvances={payingDriver.personalAdvances || []}
          weeklyFixed={
            (driverPaymentMap[payingDriver.email]?.isOwnerOperator ? weeklyInsurance + weeklyEld : 0) +
            (driverPaymentMap[payingDriver.email]?.truck || 0) +
            (driverPaymentMap[payingDriver.email]?.trailer || 0) +
            (payingDriver.personalAdvances || []).reduce((s, a) => s + (a.amount || 0), 0)
          }
          onClose={() => setPayingDriver(null)}
          onPaid={onRefresh}
        />
      )}
    </div>
  );
}